import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from '@tanstack/react-router';
import { useHasRoleIn } from '@rhino-project/core/hooks';
import { networkApiCallOnlyData } from '@rhino-project/core/lib';
import { formatDate, formatMoney, statusBadgeClass } from '../lib/format';
import type { DashboardArrival } from '../lib/harborlightTypes';

// Ported 1:1 from the source repository (client/src/pages/DashboardPage.jsx).
// The source dashboard is a stat-tile + upcoming-arrivals table view (no
// charts); per the source-fidelity contract the source look wins. Wired to the
// custom staff-only GET /api/dashboard/summary endpoint.

interface DashboardSummary {
  room_status_counts: Record<string, number>;
  reservation_status_counts: Record<string, number>;
  guest_count: number;
  upcoming_reservations: DashboardArrival[];
}

const DashboardPage = () => {
  const { owner } = useParams({ strict: false }) as { owner: string };
  const isStaff = useHasRoleIn(['staff', 'admin']);

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isStaff) return;
    networkApiCallOnlyData('/api/dashboard/summary', { method: 'get' })
      .then((data: DashboardSummary) => setSummary(data))
      .catch((err: Error) => setError(err.message));
  }, [isStaff]);

  // Staff-only page: guests are redirected to their rooms home.
  if (!isStaff) {
    return <Navigate to="/$owner/rooms" params={{ owner }} replace />;
  }

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!summary) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-secondary" role="status" />
      </div>
    );
  }

  const rooms = summary.room_status_counts ?? {};
  const reservationCounts = summary.reservation_status_counts ?? {};
  const upcoming = summary.upcoming_reservations ?? [];

  return (
    <div className="fade-up">
      <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-4">
        <div>
          <p className="text-uppercase small text-secondary mb-1">Operations</p>
          <h1 className="display-title display-5 mb-0">Front desk overview</h1>
        </div>
        <Link
          className="btn btn-sea"
          to="/$owner/reservations"
          params={{ owner }}
        >
          Manage reservations
        </Link>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-6 col-lg-3">
          <div className="stat-tile h-100">
            <div className="text-secondary small">Available</div>
            <div className="value">{rooms.AVAILABLE ?? 0}</div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="stat-tile h-100">
            <div className="text-secondary small">Occupied</div>
            <div className="value">{rooms.OCCUPIED ?? 0}</div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="stat-tile h-100">
            <div className="text-secondary small">Maintenance</div>
            <div className="value">{rooms.MAINTENANCE ?? 0}</div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="stat-tile h-100">
            <div className="text-secondary small">Guests</div>
            <div className="value">{summary.guest_count ?? 0}</div>
          </div>
        </div>
      </div>

      <div className="surface-panel p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="h4 display-title mb-0">Upcoming arrivals</h2>
          <span className="badge badge-soft rounded-pill">
            {reservationCounts.CONFIRMED || 0} confirmed
          </span>
        </div>
        {upcoming.length === 0 ? (
          <p className="text-secondary mb-0">No upcoming reservations.</p>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Guest</th>
                  <th>Room</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th>Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {upcoming.map((item: DashboardArrival) => (
                  <tr key={item.id}>
                    <td>{item.guest_name}</td>
                    <td>
                      #{item.room_number} · {item.room_type}
                    </td>
                    <td>{formatDate(item.check_in)}</td>
                    <td>{formatDate(item.check_out)}</td>
                    <td>{formatMoney(item.total_price)}</td>
                    <td>
                      <span className={statusBadgeClass(item.status)}>
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
